import classNames from 'classnames';
import React, {useState} from 'react';
import {Card} from '../Layout/Card';
import {Button} from '../../Button';

interface ICollapsableCardProps {
  className?: string;
  closeable?: boolean;
  compressed?: boolean;
  children: React.ReactNode;
  title: React.ReactNode;
  subTitle?: React.ReactNode;
  action?: React.ReactNode;
  isOpen: boolean;
  onOpen?: (open: boolean) => void;
}

export const CollapsableCard: React.FC<ICollapsableCardProps> = (props) => {
  const [open, setOpen] = useState(props.isOpen === true);
  const _openClick = () => {
    const o = !open;
    setOpen(o);
    props.onOpen && props.onOpen(o);
  };

  return (
    <Card compressed={props.compressed} className={props.className}>
      <div className="flex">
        <div
          className={classNames(
            'flex-grow font-semibold text-base',
            open ? 'pb-6' : 'pb-0',
            props.closeable !== false ? 'cursor-pointer' : '',
          )}
          onClick={props.closeable !== false ? _openClick : undefined}>
          {props.title}
          {props.subTitle && (
            <div className="font-normal text-sm">{props.subTitle}</div>
          )}
        </div>
        {props.action && <div className="text-right">{props.action}</div>}
      </div>
      {open && <div>{props.children}</div>}
    </Card>
  );
};

interface IFormCardProps extends ICollapsableCardProps {
  closeable?: boolean;
  updating: boolean;
  updateLabel?: string;
  onUpdate?: () => void;
}

export const CollapsableFormCard: React.FC<IFormCardProps> = (props) => {
  const [open, setOpen] = useState(props.isOpen === true);

  const _openClick = () => {
    if (props.updating) {
      return;
    }
    const o = !open;
    if (!o) {
      props.onUpdate && props.onUpdate();
    }
    setOpen(o);
    props.onOpen && props.onOpen(o);
  };

  return (
    <FormSectionItem>
      <CollapsableCard
        {...props}
        closeable={props.closeable}
        onOpen={_openClick}>
        <FormSection>
          {props.children}
          {props.onUpdate && (
            <FormSectionItem>
              <Button
                buttonProps={{type: 'button'}}
                disabled={props.onUpdate === undefined || props.updating}
                onClick={props.onUpdate}
                category="primary"
                label={props.updateLabel ?? 'Update'}
              />
            </FormSectionItem>
          )}
        </FormSection>
      </CollapsableCard>
    </FormSectionItem>
  );
};

interface IProps {
  children: React.ReactNode;
  disabled?: boolean;
  bottomBorder?: boolean;
}

export const FormSection: React.FC<IProps> = (props) => {
  const disabled = props.disabled ?? false;
  const bottomBorder = props.bottomBorder ?? false;
  return (
    <fieldset
      disabled={disabled}
      className={classNames(
        'pb-4',
        bottomBorder ? 'border-b border-gray-900/10' : '',
      )}>
      <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
        {props.children}
      </div>
    </fieldset>
  );
};

export interface IFormSectionItemProps {
  children: React.ReactNode;
  columnSpan?: '1' | '2' | '3' | '4' | '5' | '6';
}

const COLUMN_SPAN_MAP = {
  '1': 'sm:col-span-1',
  '2': 'sm:col-span-2',
  '3': 'sm:col-span-3',
  '4': 'sm:col-span-4',
  '5': 'sm:col-span-5',
  '6': 'sm:col-span-6',
};

export const FormSectionItem: React.FC<IFormSectionItemProps> = (props) => {
  const {columnSpan = '6'} = props;
  return <div className={COLUMN_SPAN_MAP[columnSpan]}>{props.children}</div>;
};

export const FormSubHeading: React.FC<IFormSectionItemProps> = (props) => (
  <FormSectionItem {...props}>
    <h2 className="text-md font-semibold">{props.children}</h2>
  </FormSectionItem>
);

interface IFormSpacerProps {
  columnSpan: IFormSectionItemProps['columnSpan'];
}

export const FormSpacer: React.FC<IFormSpacerProps> = (props) => {
  const {columnSpan = '6'} = props;
  return (
    <div
      className={classNames('hidden sm:block', COLUMN_SPAN_MAP[columnSpan])}
    />
  );
};
